import React from 'react';
import { Box, Container, useTheme, useMediaQuery } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

type ResponsiveContainerProps = {
  children: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  disableGutters?: boolean;
  sx?: SxProps<Theme>;
  bottomNavPadding?: boolean; // Add padding for bottom navigation on mobile
  fullHeight?: boolean; // Container takes full viewport height
  centerContent?: boolean; // Center content vertically and horizontally
};

/**
 * A responsive container component that automatically adjusts padding and layout
 * based on screen size, with special considerations for mobile devices.
 */
const ResponsiveContainer: React.FC<ResponsiveContainerProps> = ({
  children,
  maxWidth = 'lg',
  disableGutters = false,
  sx = {},
  bottomNavPadding = false,
  fullHeight = false,
  centerContent = false,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Container
      maxWidth={maxWidth}
      disableGutters={disableGutters}
      sx={{
        px: {
          xs: 2, // 16px padding on mobile
          sm: 3, // 24px padding on tablet
          md: 4, // 32px padding on desktop
        },
        pb: bottomNavPadding && isMobile ? '80px' : undefined, // Additional bottom padding on mobile for bottom nav
        height: fullHeight ? '100vh' : 'auto',
        display: fullHeight || centerContent ? 'flex' : 'block',
        flexDirection: 'column',
        justifyContent: centerContent ? 'center' : 'flex-start',
        alignItems: centerContent ? 'center' : 'stretch',
        ...sx,
      }}
    >
      {children}
    </Container>
  );
};

export default ResponsiveContainer;
